/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Notifications - Constants
 * Event IDs match ref.notification_types codes in the database.
 */

import { FileText, CheckCircle, AlertTriangle, Settings, GitPullRequest } from "lucide-react";
import type { NotificationSettings, EventCategory, SeverityLevel, DigestOption } from "./types";

// Notification event categories — IDs match ref.notification_types.code
// notification_type: "alert" = urgent/action-required, "notification" = informational
export const EVENT_CATEGORIES: Record<string, EventCategory> = {
  submissions: {
    label: "Submissions & Files",
    icon: FileText,
    events: [
      {
        id: "submission_created",
        label: "Submission Created",
        description: "New submission created",
        notification_type: "notification",
      },
      {
        id: "submission_completed",
        label: "Submission Completed",
        description: "Submission completed successfully",
        notification_type: "notification",
      },
      {
        id: "submission_failed",
        label: "Submission Failed",
        description: "Submission processing failed",
        notification_type: "alert",
      },
      {
        id: "file_uploaded",
        label: "File Uploaded",
        description: "File uploaded to submission",
        notification_type: "notification",
      },
      {
        id: "file_validated",
        label: "File Validated",
        description: "File validation completed",
        notification_type: "notification",
      },
      {
        id: "resubmission_required",
        label: "Resubmission Required",
        description: "Corrections needed, resubmission required",
        notification_type: "alert",
      },
      {
        id: "deadline_reminder",
        label: "Deadline Reminder",
        description: "Upcoming submission deadline approaching",
        notification_type: "notification",
      },
    ],
  },
  dq_runs: {
    label: "DQ Runs & Measures",
    icon: CheckCircle,
    events: [
      {
        id: "dq_run_started",
        label: "DQ Run Started",
        description: "Data quality run started",
        notification_type: "notification",
      },
      {
        id: "dq_run_completed",
        label: "DQ Run Completed",
        description: "Data quality run completed",
        notification_type: "notification",
      },
      {
        id: "measure_failed",
        label: "Measure Failed",
        description: "DQ measure exceeded threshold",
        notification_type: "alert",
      },
      {
        id: "measure_flagged",
        label: "Measure Flagged",
        description: "Measure flagged for review",
        notification_type: "alert",
      },
      {
        id: "threshold_breach",
        label: "Threshold Breach",
        description: "Critical quality threshold violated",
        notification_type: "alert",
      },
    ],
  },
  incidents: {
    label: "Incidents",
    icon: AlertTriangle,
    events: [
      {
        id: "incident_created",
        label: "Incident Created",
        description: "New incident requiring attention",
        notification_type: "alert",
      },
      {
        id: "incident_assigned",
        label: "Incident Assigned",
        description: "Incident assigned to user",
        notification_type: "notification",
      },
      {
        id: "incident_resolved",
        label: "Incident Resolved",
        description: "Incident resolved",
        notification_type: "notification",
      },
      {
        id: "escalation",
        label: "Escalation",
        description: "Issue escalated to higher authority for resolution",
        notification_type: "alert",
      },
    ],
  },
  change_requests: {
    label: "Change Requests & Approvals",
    icon: GitPullRequest,
    events: [
      {
        id: "change_request_submitted",
        label: "Change Request Submitted",
        description: "Change request submitted for review",
        notification_type: "notification",
      },
      {
        id: "change_request_approved",
        label: "Change Request Approved",
        description: "Change request approved",
        notification_type: "notification",
      },
      {
        id: "change_request_rejected",
        label: "Change Request Rejected",
        description: "Change request rejected",
        notification_type: "notification",
      },
      {
        id: "approval_required",
        label: "Approval Required",
        description: "Action requires approval",
        notification_type: "alert",
      },
    ],
  },
  system: {
    label: "System & Reports",
    icon: Settings,
    events: [
      {
        id: "system_alert",
        label: "System Alert",
        description: "System-level alert",
        notification_type: "alert",
      },
      {
        id: "quality_report",
        label: "Quality Report",
        description: "Periodic quality summary report",
        notification_type: "notification",
      },
      {
        id: "cms_updates",
        label: "CMS Updates",
        description: "Policy or threshold changes from CMS",
        notification_type: "notification",
      },
    ],
  },
};

// Severity levels
export const SEVERITY_LEVELS: SeverityLevel[] = [
  {
    id: "critical",
    label: "Critical",
    color: "bg-red-500",
    description: "Immediate action required",
  },
  { id: "high", label: "High", color: "bg-orange-500", description: "Important, needs attention" },
  { id: "medium", label: "Medium", color: "bg-yellow-500", description: "Should be reviewed soon" },
  { id: "low", label: "Low", color: "bg-blue-500", description: "Informational" },
];

// Digest frequency options
export const DIGEST_OPTIONS: DigestOption[] = [
  { value: "realtime", label: "Real-time", description: "Send immediately" },
  { value: "hourly", label: "Hourly Digest", description: "Group notifications hourly" },
  { value: "daily", label: "Daily Digest", description: "Send daily summary" },
  { value: "weekly", label: "Weekly Digest", description: "Send weekly summary" },
];

// Default notification settings — event IDs match ref.notification_types.code
// No events pre-selected; user chooses which notifications they want per channel.
export const DEFAULT_SETTINGS: NotificationSettings = {
  email: {
    enabled: true,
    recipients: [],
    events: [],
    severities: ["critical", "high"],
    digestFrequency: "realtime",
    includeDetails: true,
  },
  teams: {
    enabled: false,
    webhookUrl: "",
    events: [],
    severities: ["critical", "high"],
    includeAdaptiveCard: true,
  },
  schedule: {
    quietHoursEnabled: true,
    quietHoursStart: "22:00",
    quietHoursEnd: "07:00",
    overrideForCritical: true,
    timezone:
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC",
  },
};

export const STORAGE_KEY = "torqe_notification_settings";
